// The approver's side of an early clock-out: someone is standing at the door.
//
// This card renders nothing at all when there is nothing to decide, so it costs
// a manager with no requests exactly one line of nothing on the Attendance page.
// When it does render, it leads with the two facts a decision turns on — how
// early, and why — because the whole point of the gate is that somebody looks
// at those before the person leaves.
//
// Declining takes the same optional note as approving. A decline with no word
// back is the version of this feature people hate.

import { useCallback, useEffect, useState } from 'react';
import { Check, DoorOpen, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  decideEarlyExitRequest,
  listEarlyExitRequests,
  type EarlyExitRequestRow,
} from '../../api/attendance';
import { useToast } from '../../hooks/useToast';

interface Props {
  /** Bump to re-fetch (a decision elsewhere, a websocket nudge, a reload). */
  reloadKey?: number;
}

const timeOf = (stamp: string | null) => {
  if (!stamp) return '';
  const at = new Date(stamp);
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default function EarlyExitApprovals({ reloadKey = 0 }: Props) {
  const [rows, setRows] = useState<EarlyExitRequestRow[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const { requests } = await listEarlyExitRequests({
        scope: 'approvals',
        status: 'PENDING',
      });
      setRows(requests);
    } catch {
      // An approvals queue that cannot load must not take the attendance page
      // down with it — the requests are still on the bell and in the list.
      setRows([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const decide = async (row: EarlyExitRequestRow, decision: 'approve' | 'reject') => {
    setBusyId(row.id);
    try {
      await decideEarlyExitRequest(row.id, decision, notes[row.id]);
      // Drop it locally rather than re-fetching: the row is gone from the queue
      // either way, and the list must not flicker back in while the call lands.
      setRows((current) => current.filter((r) => r.id !== row.id));
      toast({
        title: decision === 'approve' ? 'Approved' : 'Declined',
        description:
          decision === 'approve'
            ? `${row.employee_name || 'They'} can clock out now.`
            : `${row.employee_name || 'They'} have been told.`,
      });
    } catch (err: any) {
      toast({
        title: "Couldn't record that",
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <DoorOpen className="h-4 w-4 text-amber-700" />
        <h3 className="text-sm font-semibold text-gray-900">
          Early clock-out requests
        </h3>
        <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
          {rows.length} waiting
        </span>
      </div>

      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className="rounded-lg border border-amber-200 bg-white p-3"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-sm font-semibold text-gray-900">
                {row.employee_name || 'An employee'}
              </span>
              <span className="text-xs font-medium text-amber-700">
                {row.early_by}
              </span>
              {row.shift_end && (
                <span className="text-xs text-gray-500">
                  shift ends {row.shift_end}
                </span>
              )}
              {row.auto_raised && (
                // allow & flag: they already left. Say so plainly, or an
                // approval here reads as permission that was never asked for.
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                  already left
                </span>
              )}
              <span className="ml-auto text-[11px] text-gray-400">
                asked {timeOf(row.created_at)}
              </span>
            </div>

            <p className="mt-1 text-sm text-gray-700">
              {row.reason || <span className="text-gray-400">No reason given</span>}
            </p>

            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                value={notes[row.id] || ''}
                onChange={(e) =>
                  setNotes((current) => ({ ...current, [row.id]: e.target.value }))
                }
                placeholder="Note back (optional)"
                maxLength={500}
                className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => decide(row, 'approve')}
                  disabled={busyId === row.id}
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  <Check className="mr-1 h-3.5 w-3.5" /> Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => decide(row, 'reject')}
                  disabled={busyId === row.id}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Decline
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
